/**
 * A room on a phone and an iPad, in the Season design. A phone opens the room as an overview with its five sections,
 * each a screen of its own; upright puts a switch of sections under the room's card; on its side the sections stand
 * beside the open one, with the right pane that belongs to it. It reads the same room and does the same things as the
 * computer's page.
 */
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { useAuth } from "../../auth/AuthContext";
import { ChangeTime, moveLiveTime, timeSavedLine } from "../../board/ChangeTime";
import { boardPath } from "../../board/paths";
import { InviteSheet, invitable, inviteTargetOf } from "../../components/InviteSheet";
import { DockedBar } from "../../components/season/DockedBar";
import { Empty } from "../../components/season/Empty";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { Pane, Panes } from "../../components/season/Panes";
import { PushedNav } from "../../components/season/PushedNav";
import { SeasonCard } from "../../components/season/Card";
import { Switch } from "../../components/season/Switch";
import { Button } from "../../components/ui/Button";
import { Sheet } from "../../components/ui/Sheet";
import { useToast } from "../../components/ui/Toast";
import "../../styles/season-rooms.css";
import { longDayTime } from "./liveWords";
import { RecordingStrip } from "./RecordingStrip";
import { ReplaySheet } from "./ReplaySheet";
import { CHANGE_TIME_STATE, copyRoomLink, movableLive } from "./RoomActions";
import { callPath, draftLine, type RoomTab, stateLine, tabPath, tabsFor, whenLine } from "./roomWords";
import { AudienceAside, AudienceSection } from "./SeasonAudience";
import { FollowupsSection } from "./SeasonFollowups";
import { PagePhone, PageSection } from "./SeasonPage";
import { ReportMain, ReportRest, ReportSheets } from "./SeasonReport";
import { RoomMore } from "./SeasonRoomMenu";
import { AcrossSeries, SessionsSection } from "./SeasonSessions";
import { LinkRow, RoomPicture, SECTIONS, SectionRows, sectionPath } from "./seasonRoomParts";
import { RoomTalkCard } from "./RoomTalk";
import { useRoomPage, useRoomTalk } from "./useRoomPage";
import { useSessionReport } from "./useSessionReport";
import { useSessions } from "./sessionsParts";

type PageData = ReturnType<typeof useRoomPage>;
type Detail = NonNullable<PageData["detail"]>;

export function SeasonRoom({ layout }: { layout: SeasonLayout }) {
  const { id = "", tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const page = useRoomPage(id);
  const { detail, phase } = page;
  if (phase === "error" && !detail) {
    return (
      <Page layout={layout} narrow className="rm">
        <PushedNav back={{ to: "/rooms", label: "Rooms" }} />
        <Empty title="We couldn't find that room">
          <Button onClick={() => navigate("/rooms")}>Back to Rooms</Button>
        </Empty>
      </Page>
    );
  }
  if (!detail) {
    return (
      <Page layout={layout} narrow className="rm">
        <PushedNav back={{ to: "/rooms", label: "Rooms" }} />
        <p className="k-sub" aria-busy="true">
          Loading
        </p>
      </Page>
    );
  }
  if (detail.quick_live) return <Navigate to={callPath(detail.id)} replace />;
  return <OpenRoom key={detail.id} layout={layout} page={page} detail={detail} rawTab={tab} />;
}

function OpenRoom({ layout, page, detail, rawTab }: { layout: SeasonLayout; page: PageData; detail: Detail; rawTab: string | undefined }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { series, last, lastLive, across, askAcrossAgain, onSaved, reload } = page;
  const talk = useRoomTalk(detail.id);
  const [inviting, setInviting] = useState(false);
  const [changing, setChanging] = useState(false);
  const [playing, setPlaying] = useState(false);
  const report = useSessionReport(detail.id, lastLive, !!series);
  const sessions = useSessions(detail, detail.live, onSaved);
  const hostFirst = user?.first_name ?? "";

  // The room's menu can ask for the time to be changed from another screen.
  useEffect(() => {
    if ((location.state as typeof CHANGE_TIME_STATE | null)?.changeTime) {
      setChanging(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const live = detail.live;
  const tabs = tabsFor(detail);
  const section = (tabs.find((t) => t.key === rawTab)?.key ?? null) as RoomTab | null;
  if (rawTab && !section) return <Navigate to={`/rooms/${detail.id}`} replace />;
  const active: RoomTab = section ?? "audience";
  const draft = detail.status === "draft" && detail.board;
  const room = { ...detail, registered: detail.registered_here };
  const when = live?.series ? live.series.long : whenLine({ ...detail, schedule_label: detail.schedule?.label ?? "", live });
  // Why no length: the picture already says it.
  const whenLineText = when;
  const state = stateLine({ status: detail.status, live: live && { ...live, replayPlays: live.replay.playable }, watching: detail.watching.length }, Date.now());
  const moving = movableLive(room) ? (live ?? null) : null;
  const openChange = () => (series ? navigate(tabPath(detail.id, "sessions")) : setChanging(true));
  const canInvite = invitable(room);
  const invite = () => setInviting(true);
  const waiting = report.waiting.length;

  const more = (
    <RoomMore
      room={room}
      layout={layout}
      onChanged={reload}
      onDeleted={() => navigate("/rooms", { replace: true })}
      onChangeTime={openChange}
      onTab={(t) => navigate(tabPath(detail.id, t))}
    />
  );
  const buttons = (
    <div className="rm-btns">
      {canInvite && layout === "upright" ? (
        <Button variant="p" icon="userplus" onClick={invite}>
          Invite people
        </Button>
      ) : null}
      <Button icon="link" onClick={() => void copyRoomLink(detail.register_url, toast)}>
        Copy link
      </Button>
      {detail.status === "published" ? (
        <Button icon="eye" onClick={() => window.open(detail.register_url, "_blank", "noopener")}>
          Preview
        </Button>
      ) : null}
      {more}
    </div>
  );
  const strip = !live ? (
    <RecordingStrip detailId={detail.id} videoId={detail.video_id} state={detail.video_state} durationSec={detail.duration_sec} onReady={reload} />
  ) : null;

  const sheets = (
    <>
      {inviting ? (
        <InviteSheet
          webinar={inviteTargetOf(detail)}
          onClose={() => setInviting(false)}
          onSent={(send) => toast(`Sending ${send.total.toLocaleString("en-US")} ${send.total === 1 ? "invite" : "invites"}`)}
          onToast={toast}
        />
      ) : null}
      {playing ? (
        <ReplaySheet
          webinarId={detail.id}
          {...(last ? { sessionId: last.id } : live ? { sessionId: live.sessionId } : {})}
          title={detail.title}
          onClose={() => setPlaying(false)}
        />
      ) : null}
      {changing && moving ? (
        <Sheet open onClose={() => setChanging(false)} title="Change the time">
          <div className="rm-time">
            <ChangeTime
              live={moving}
              seats={moving.registered}
              align="start"
              onSave={async (at, repeats) => {
                const moved = await moveLiveTime(detail.id, at, repeats);
                if (typeof moved === "string") return moved;
                toast(timeSavedLine(moved));
                reload();
                return "";
              }}
              onClose={() => setChanging(false)}
            />
          </div>
        </Sheet>
      ) : null}
      {lastLive ? <ReportSheets webinarId={detail.id} title={detail.title} r={report} /> : null}
    </>
  );

  // --- a draft: made on its board, so the page says how far it has got and goes back there ---
  if (draft) {
    const progress = draftLine(detail, Date.now());
    const go = () => navigate(boardPath(detail.id));
    if (layout === "side") {
      return (
        <>
          <Panes className="rm-split">
            <Pane kind="list" label="The room">
              <Page layout="side" className="rm">
                <PushedNav back={{ to: "/rooms", label: "Rooms" }} />
                <RoomPicture room={{ ...detail, live }} />
                <h2 className="k-h1" dir="auto">{detail.title}</h2>
                <p className="k-meta rm-when">{progress}</p>
              </Page>
            </Pane>
            <Pane kind="main" label="The board">
              <Page layout="side" className="rm">
                <div className="rm-btns">
                  <Button variant="p" after="right" onClick={go}>
                    Continue on the board
                  </Button>
                  {more}
                </div>
              </Page>
            </Pane>
          </Panes>
          {sheets}
        </>
      );
    }
    if (layout === "upright") {
      return (
        <Page layout={layout} narrow className="rm">
          <PushedNav back={{ to: "/rooms", label: "Rooms" }} />
          <SeasonCard className="rm-hdr">
            <span className="rm-hp">
              <RoomPicture room={{ ...detail, live }} />
            </span>
            <div className="rm-ht">
              <h1 className="k-h1" dir="auto">{detail.title}</h1>
              <p className="k-meta rm-when">{progress}</p>
              <div className="rm-btns">
                <Button variant="p" after="right" onClick={go}>
                  Continue on the board
                </Button>
                {more}
              </div>
            </div>
          </SeasonCard>
          {sheets}
        </Page>
      );
    }
    return (
      <Page layout={layout} className="rm">
        <PushedNav back={{ to: "/rooms", label: "Rooms" }} right={more} />
        <RoomPicture room={{ ...detail, live }} />
        <h1 className="k-h1" dir="auto">{detail.title}</h1>
        <p className="k-meta rm-when">{progress}</p>
        <DockedBar>
          <Button variant="p" full after="right" onClick={go}>
            Continue on the board
          </Button>
        </DockedBar>
        {sheets}
      </Page>
    );
  }

  // --- the sections ---
  const lastTitle = lastLive ? longDayTime(lastLive.startsAt, lastLive.timezone) : "";
  const body = (which: RoomTab, at: SeasonLayout) => {
    if (which === "live") {
      if (!lastLive) return <p className="k-rm">Getting the last live</p>;
      return (
        <>
          {at === "phone" ? null : (
            <>
              <h2 className="rm-h2">{lastTitle}</h2>
              <p className="k-rm rm-hs">Session {lastLive.seq}</p>
            </>
          )}
          <ReportMain live={lastLive} r={report} layout={at} webinarId={detail.id} />
          {at === "side" ? null : <ReportRest live={lastLive} r={report} layout={at} />}
        </>
      );
    }
    if (which === "sessions") {
      return live?.series ? (
        <SessionsSection detail={detail} live={live} across={across} state={sessions} layout={at} hostFirst={hostFirst} onSent={askAcrossAgain} onSaved={onSaved} />
      ) : null;
    }
    if (which === "page") return <PageSection detail={detail} layout={at} onChanged={reload} />;
    if (which === "followups") return <FollowupsSection detail={detail} />;
    return <AudienceSection detail={detail} layout={at} hostFirst={hostFirst} talk={talk} />;
  };

  if (layout === "phone") {
    if (!section) {
      return (
        <Page layout={layout} className="rm">
          <PushedNav back={{ to: "/rooms", label: "Rooms" }} right={more} />
          <RoomPicture room={{ ...detail, live }} />
          <h1 className="k-h1" dir="auto">{detail.title}</h1>
          <p className="k-meta rm-when">{whenLineText}</p>
          {state ? <p className={`k-meta rm-st ${state.tone}`}>{state.text}</p> : null}
          {strip}
          <LinkRow url={detail.register_url} />
          <SectionRows detail={detail} lastLive={lastLive} talk={talk} />
          {canInvite ? (
            <DockedBar>
              <Button variant="p" full icon="userplus" onClick={invite}>
                Invite people
              </Button>
            </DockedBar>
          ) : null}
          {sheets}
        </Page>
      );
    }
    const ctx = section === "live" && lastLive ? `${lastTitle} · Session ${lastLive.seq}` : detail.title;
    const dock =
      section === "audience" && canInvite ? (
        <Button variant="p" full icon="userplus" onClick={invite}>
          Invite people
        </Button>
      ) : section === "live" && waiting ? (
        <Button variant="p" full icon="sparkle" onClick={() => report.setReplying(true)}>
          Send replies
        </Button>
      ) : null;
    return (
      <Page layout={layout} className="rm">
        <PushedNav back={{ to: `/rooms/${detail.id}`, label: "Back" }} right={more} />
        <h1 className="k-h1">{SECTIONS[section].label}</h1>
        <p className="k-meta rm-ctx">{ctx}</p>
        <div className="rm-body">{body(section, layout)}</div>
        {dock ? <DockedBar>{dock}</DockedBar> : null}
        {sheets}
      </Page>
    );
  }

  if (layout === "upright") {
    return (
      <Page layout={layout} narrow className="rm">
        <PushedNav back={{ to: "/rooms", label: "Rooms" }} />
        <SeasonCard className="rm-hdr">
          <span className="rm-hp">
            <RoomPicture room={{ ...detail, live }} />
          </span>
          <div className="rm-ht">
            <p className="k-rm">{whenLineText}</p>
            <h1 className="k-h1" dir="auto">{detail.title}</h1>
            <LinkRow url={detail.register_url} />
            {buttons}
          </div>
        </SeasonCard>
        {strip}
        <Switch
          label="The room"
          value={active}
          onChange={(k) => navigate(sectionPath(detail.id, k))}
          options={tabs.map((t) => ({ value: t.key, label: t.label }))}
        />
        <div className="rm-body">{body(active, layout)}</div>
        {sheets}
      </Page>
    );
  }

  // The iPad on its side: the sections, the open one, and what belongs beside it.
  const asideRest =
    active === "audience" ? (
      <AudienceAside detail={detail} onInvite={invite} onWatchReplay={() => setPlaying(true)} />
    ) : active === "live" && lastLive ? (
      <ReportRest live={lastLive} r={report} layout="side" />
    ) : active === "sessions" && live?.series ? (
      <AcrossSeries detail={detail} live={live} across={across} state={sessions} layout="side" hostFirst={hostFirst} />
    ) : active === "page" ? (
      <PagePhone detail={detail} />
    ) : null;
  // Why only where a right pane already exists: Follow-ups has none, and the card does not make one.
  const aside = asideRest ? (
    <>
      {talk ? <RoomTalkCard row={talk} /> : null}
      {asideRest}
    </>
  ) : null;
  return (
    <>
      <Panes three={!!aside} className="rm-split">
        <Pane kind="list" keep="room-sections" label="The room">
          <Page layout="side" className="rm">
            <PushedNav back={{ to: "/rooms", label: "Rooms" }} />
            <RoomPicture room={{ ...detail, live }} />
            <h2 className="k-h1 rm-lt" dir="auto">{detail.title}</h2>
            <p className="k-meta rm-when">{whenLineText}</p>
            <LinkRow url={detail.register_url} />
            {buttons}
            <SectionRows detail={detail} lastLive={lastLive} on={active} />
          </Page>
        </Pane>
        <Pane kind="main" showing={`${detail.id}:${active}`} label={SECTIONS[active].label}>
          <Page layout="side" className="rm">
            {strip}
            <h2 className="rm-mt">{SECTIONS[active].label}</h2>
            {body(active, "side")}
          </Page>
        </Pane>
        {aside ? (
          <Pane kind="detail" showing={`${detail.id}:${active}`} label="Beside the open section">
            <Page layout="side" className="rm">
              {aside}
            </Page>
          </Pane>
        ) : null}
      </Panes>
      {sheets}
    </>
  );
}
